import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { confirmPasswordReset, fetchMe, login, logout, requestPasswordReset } from './api.js'

export const meQueryKey = ['me'] as const

/**
 * `data === null` means "not signed in" — a normal state `RequireAuth`
 * redirects on, not a fetch failure. `fetchMe` already converts a 401 into
 * `null`; anything that reaches here as `isError` is a real problem (the
 * server unreachable, a 500), which is deliberately kept distinct so the app
 * can tell "please sign in" apart from "something is broken" instead of
 * showing the login screen for both.
 */
export function useMe() {
  return useQuery({
    queryKey: meQueryKey,
    queryFn: fetchMe,
    staleTime: 60_000,
  })
}

export function useLoginMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: login,
    onSuccess: (identity) => {
      queryClient.setQueryData(meQueryKey, identity)
    },
  })
}

export function useLogoutMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: logout,
    onSuccess: () => {
      queryClient.setQueryData(meQueryKey, null)
    },
  })
}

export function useRequestPasswordResetMutation() {
  return useMutation({ mutationFn: requestPasswordReset })
}

export function useConfirmPasswordResetMutation() {
  return useMutation({ mutationFn: confirmPasswordReset })
}
